import { Recurrence, SelfServiceSchedulePane, StaticInitializers } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, HostedStackedElementContainer, InlineAlertType, InlineAlertWidget, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, RadioLayoutType } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { cssClass, Style } from '../../style';
import { ALL_REFRESH_TASK_TYPES, RefreshTargetType, RefreshTaskType } from '../../ts/ServerApi/requests/CreateSelfServiceScheduleExtractTasksRequest';

export function localizeRefreshTargetType(type: RefreshTargetType): string {
  switch (type) {
    case 'Datasource':
      return Messages.datasource();
    case 'Workbook':
      return Messages.workbook();
  }
}

export function localizeRefreshTaskType(type: RefreshTaskType): string {
  switch (type) {
    case 'IncrementExtract':
      return Messages.incrementalRefresh();
    case 'RefreshExtract':
      return Messages.fullRefresh();
  }
}

export interface RefreshSelfServiceScheduleDialogProps {
  recurrence: Recurrence;
  targetType: RefreshTargetType;
  quantity: number;
  isNew?: boolean;
  name?: string;
  taskType?: RefreshTaskType;
  disableSelectingTaskType?: boolean;
  close: () => void;
  confirm: (recurrence: Recurrence, taskType: RefreshTaskType, scheduleName: string) => void;
  showExistingTasksWillBeDeletedWarning?: boolean;
}

export interface RefreshSelfServiceScheduleDialogState {
  recurrence: Recurrence;
  taskTypeSelectedIndex?: number;
}

// A dictionary instead of just having the params passed in 1 by 1 for readability
export interface OpenRefreshSelfServiceScheduleDialogParameters {
  save: (recurrence: Recurrence, taskType: RefreshTaskType, scheduleName: string) => Promise<IResult>;
  quantity: number;
  targetType: RefreshTargetType;
  isNew?: boolean;
  name?: string;
  schedule?: IScheduleInfo;
  taskType?: RefreshTaskType;
  disableSelectingTaskType?: boolean;
  showExistingTasksWillBeDeletedWarning?: boolean;
}

export class RefreshSelfServiceScheduleDialog extends React.Component<RefreshSelfServiceScheduleDialogProps, RefreshSelfServiceScheduleDialogState> {
  public static DIALOG_SELECTOR = 'edit-schedule-inner';

  public static async open(params: OpenRefreshSelfServiceScheduleDialogParameters): Promise<IResult> {
    return new Promise((resolve, reject) => {
      const hostContainer = new HostedStackedElementContainer();
      const close = () => {
        hostContainer && hostContainer.remove();
      };
      const existingRecurrence = params.schedule && IScheduleInfo.toSSSRecurrence(params.schedule);
      const taskType = params.taskType
        ? params.taskType // Use specified task type if provided
        : params.isNew
          ? 'IncrementExtract' // Otherise default to incremental if new
          : undefined;  // If not new then don't specify task type
      const props: RefreshSelfServiceScheduleDialogProps = {
        ...params,
        recurrence: existingRecurrence || StaticInitializers.defaultRecurrence(),
        taskType,
        showExistingTasksWillBeDeletedWarning: params.showExistingTasksWillBeDeletedWarning,
        close,
        confirm: (recurrence: Recurrence, taskType: RefreshTaskType, scheduleName: string) => {
          // The confirm function should resolve the promise so that the caller to open can await on it and handle it
          // Note that even when saving fails, it is expect the promise will have a "result" and not an "error".
          // Specifically it will have a list of IError[] (see IResult)
          close();
          params.save(recurrence, taskType, scheduleName)
            .then(result => resolve(result))
            .catch(error => reject(error));
        }
      };
      const dialog = React.createElement(RefreshSelfServiceScheduleDialog, props);
      hostContainer.render(dialog);
    });
  }

  constructor(props: RefreshSelfServiceScheduleDialogProps) {
    super(props);
    this.state = {
      recurrence: props.recurrence,
      taskTypeSelectedIndex: props.taskType && ALL_REFRESH_TASK_TYPES.findIndex(type => type === props.taskType),
    };
  }

  // It's its own method to avoid bloating the render method too much
  public renderFooter() {
    return <DialogFooterButtonsWidget
      buttons={[
        {
          handleClick: () => this.props.close(),
          buttonType: ButtonType.Outline,
          children: Messages.cancel(),
          testId: 'footer-cancel'
        },
        {
          handleClick: () => this.props.confirm(
            this.state.recurrence,
            ALL_REFRESH_TASK_TYPES[this.state.taskTypeSelectedIndex],
            IScheduleInfo.getRecurrenceDescription(this.state.recurrence)
          ),
          buttonType: ButtonType.Go,
          children: this.props.isNew
            ? Messages.refreshSelfServiceScheduleConfirmCreate()
            : Messages.refreshSelfServiceScheduleConfirmEdit(),
          testId: 'footer-confirm',
        },
      ]}
      testId="refresh-sss-dialog-footer"
    />;
  }

  // public so tests can hit it
  public getTaskTypeOptions(): RadioButtonWithLabelOption<string>[] {
    return ALL_REFRESH_TASK_TYPES.map((type: RefreshTaskType) => ({
      value: type,
      testId: `radio-button-${type}`,
      label: localizeRefreshTaskType(type),
      disabled: this.props.disableSelectingTaskType && (type !== this.props.taskType),
    }));
  }

  public render(): React.ReactNode {
    const title = this.props.isNew
      ? Messages.refreshSelfServiceScheduleCreateTitle()
      : Messages.refreshSelfServiceScheduleEditTitle();
    const server = getSessionManagementServiceInstance();
    const userAccountSettingsLink = new SiteUrls({
      siteUrlName: server.sessionSiteUrlName(),
      queryString: window.location.search
    }).userSettingsUrl(server.sessionUserInfo().username, server.sessionUserInfo().domainName);
    const timeZoneDetails = {
      timeZonesEnabled: true,
      timeZoneDisplayName: server.userTimeZone().displayName,
      linkToUserSettings: userAccountSettingsLink,
    };
    // Unused if taskType is not defined in props
    const taskTypeItems: RadioButtonWithLabelOption<string>[] = this.getTaskTypeOptions();
    const baseHeaderStyle: Style = {
      fontWeight: 'normal',
      fontSize: 16,
      margin: '0',
      height: '20px',
      lineHeight: '20px',
      paddingBottom: '8px',
    };
    const typeHeaderStyle = cssClass('refresh-dialog', 'type-header', baseHeaderStyle);
    const frequencyHeaderStyle = cssClass('refresh-dialog', 'frequency-header', baseHeaderStyle, {
      paddingTop: '8px',
    });
    return <DialogWidget
      testId={RefreshSelfServiceScheduleDialog.DIALOG_SELECTOR + '-outer'}
      type={DialogType.NormalWithTitleBar}
      title={title}
      onCloseRequest={this.props.close}
      showCloseButton={true}
      positioningBehavior={DialogPositioningBehavior.Draggable}
      isModal={true}
      showFooter={true}
      handleGlassClick={this.props.close}
      isGlassTransparent={false}
      footerContent={this.renderFooter()}
      dialogWidth={500}
    >
      <div data-tb-test-id={RefreshSelfServiceScheduleDialog.DIALOG_SELECTOR}>
        {this.props.taskType && <>
          <h1 className={typeHeaderStyle}>{Messages.refreshSelfServiceScheduleRefreshType()}</h1>
          <RadioButtonWithLabelGroupWidget
            ariaLabel='The type of extract to schedule'
            buttonList={taskTypeItems}
            checkedIndex={this.state.taskTypeSelectedIndex}
            handleChange={(_: string, checkedIndex: number) => {
              this.setState({
                taskTypeSelectedIndex: checkedIndex,
              });
            }}
            layout={RadioLayoutType.Veritical}
          />
        </>}
        {this.props.taskType && this.props.disableSelectingTaskType && <InlineAlertWidget
          testId='increment-unavailable-alert'
          type={InlineAlertType.Info}
          children={Messages.refreshSelfServiceScheduleIncrementExtractsUnavailable()}
        />}
        <h1 className={frequencyHeaderStyle}>{Messages.refreshSelfServiceScheduleRefreshFrequency()}</h1>
        <span data-tb-test-id='refresh-dialog-recurrence-description'>
          {IScheduleInfo.getRecurrenceDescription(this.state.recurrence)}
        </span>
        <div style={{ marginTop: -36 }}>
          <SelfServiceSchedulePane
            recurrenceModel={this.state && this.state.recurrence}
            recurrenceModelChangedCallback={(recurrence) => this.setState({ recurrence })}
            timeZoneDetails={timeZoneDetails}
            type={SelfServiceSchedulePaneType.Default}
            uncollapsible={true}
            onTimeZoneClick={this.props.close}
          />
        </div>
        {this.props.showExistingTasksWillBeDeletedWarning && <InlineAlertWidget
          testId='remote-schedules-delete-alert'
          type={InlineAlertType.Warning}
          children={Messages.remoteRefreshSwitchLocationWarning()}
        />}
      </div>
    </DialogWidget>;
  }
}